import {
  Box,
  Checkbox,
  FormControlLabel,
  FormGroup,
  Typography,
} from "@mui/material";
import React, { useMemo } from "react";
import Rating from "@mui/material/Rating";
import { useDispatch, useSelector } from "react-redux";
import {
  setAllowPost,
  setPostAnoumous,
} from "../../store/slice/feedback.slice";
import { tekst as jmTekst, vertaal as jmVertaal } from "../../lib/woorden";

const ScoreBar = ({ value, max = 10, color = "#f37120" }) => (
  <Box sx={{ width: "100%", mt: 1.5 }}>
    <Box
      sx={{
        height: 6,
        borderRadius: 99,
        background: "rgba(0,0,0,0.07)",
        overflow: "hidden",
      }}
    >
      <Box
        sx={{
          height: "100%",
          width: `${(value / max) * 100}%`,
          borderRadius: 99,
          background: color,
          transition: "width 0.6s cubic-bezier(0.34,1.56,0.64,1)",
        }}
      />
    </Box>
  </Box>
);

const YourDetails = ({ Css }) => {
  const dispatch = useDispatch();
  const { addFields, isAllowPost, postAnoumous, basicLanguages } = useSelector(
    (s) => s.feedback
  );

  const averageRating = useMemo(() => {
    const rawSum = addFields?.reduce((acc, f) => acc + f?.rating * 2, 0) ?? 0;
    return addFields?.length === 1
      ? rawSum
      : Math.round(rawSum / (addFields?.length || 1));
  }, [addFields]);

  const accentColor = Css?.feedbackHighlightCircleBgClr || "#f37120";

  const checkboxItems = useMemo(
    () => [
      {
        value: isAllowPost,
        action: setAllowPost,
        label: basicLanguages?.postFeedback,
      },
      {
        value: postAnoumous,
        action: setPostAnoumous,
        label: basicLanguages?.PostAnonymously,
      },
    ],
    [isAllowPost, postAnoumous, basicLanguages]
  );

  return (
    <Box sx={{ px: { xs: 2, md: 4 }, pt: { xs: 3, md: 4 }, pb: 2 }}>
      {/* ── Header ── */}
      <Box sx={{ mb: 4 }}>
        <Typography
          sx={{
            color: Css?.feedbackHeadingClr || "#111827",
            fontSize: { xs: "22px", md: "26px" },
            fontWeight: 700,
            letterSpacing: "-0.02em",
            lineHeight: 1.2,
            mb: 1,
          }}
        >
          {basicLanguages?.feedbackHeader}
        </Typography>
        <Typography
          sx={{
            color: Css?.feedbackInfoClr || "#6b7280",
            fontSize: { xs: "13px", md: "14px" },
            lineHeight: 1.7,
            maxWidth: 520,
          }}
        >
          {basicLanguages?.yourDetailLoremIpsum}
        </Typography>
      </Box>

      {/* ── Score card ── */}
      <Box
        sx={{
          borderRadius: "16px",
          border: `1px solid ${Css?.rationBoxBorderClr || "rgba(0,0,0,0.08)"}`,
          backgroundColor: Css?.ratingBoxBgClr || "#fff",
          p: 3,
          mb: 4,
          display: "flex",
          alignItems: "center",
          gap: 3,
          flexWrap: "wrap",
          boxShadow: "0 2px 12px rgba(0,0,0,0.05)",
        }}
      >
        {/* Big score badge */}
        <Box
          sx={{
            minWidth: 90,
            textAlign: "center",
            px: 3,
            py: 2,
            borderRadius: "12px",
            background: `linear-gradient(135deg, ${accentColor}18, ${accentColor}08)`,
            border: `1px solid ${accentColor}30`,
          }}
        >
          <Typography
            sx={{
              fontSize: "32px",
              fontWeight: 800,
              color: accentColor,
              lineHeight: 1,
            }}
          >
            {averageRating}
          </Typography>
          <Typography sx={{ fontSize: "11px", color: "#9ca3af", mt: 0.3 }}>/ 10</Typography>
        </Box>

        {/* Stars + bar */}
        <Box sx={{ flex: 1, minWidth: 180 }}>
          <Rating
            value={averageRating / 2}
            precision={0.5}
            readOnly
            sx={{ color: accentColor, mb: 0.5 }}
          />
          <Typography sx={{ fontSize: "13px", color: Css?.ratingPercentaageClr || "#374151", fontWeight: 500, mb: 0.5 }}>
            {jmVertaal("Overall satisfaction")}
          </Typography>
          <ScoreBar value={averageRating} color={accentColor} />
        </Box>
      </Box>

      {/* ── Checkboxes ── */}
      <FormGroup sx={{ gap: 1 }}>
        {checkboxItems.map(({ value, action, label }, i) => (
          <FormControlLabel
            key={i}
            control={
              <Checkbox
                size="small"
                checked={value}
                onChange={() => dispatch(action(!value))}
                sx={{
                  color: "rgba(0,0,0,0.25)",
                  "&.Mui-checked": {
                    color: Css?.checkBoxTickClr || accentColor,
                  },
                }}
              />
            }
            label={
              <Typography
                sx={{
                  color: Css?.feedbackInfoClr || "#374151",
                  fontSize: { xs: "12px", sm: "14px" },
                  lineHeight: 1.5,
                }}
              >
                {label}
              </Typography>
            }
            sx={{
              m: 0,
              p: 1.5,
              borderRadius: "10px",
              border: "1px solid",
              borderColor: value ? `${accentColor}40` : "transparent",
              background: value ? `${accentColor}06` : "transparent",
              transition: "all 0.2s",
            }}
          />
        ))}
      </FormGroup>
    </Box>
  );
};

export default YourDetails;

